import * as THREE from 'three';
import { clamp } from '../Util';

export interface LookAroundControlsProps {
  hspeed?: number;
  vspeed?: number;
  zoomStep?: number;
  minFov?: number;
  maxFov?: number;
}

const ZERO_LIMIT = 0.000001;

export class LookAroundControls extends THREE.EventDispatcher {
  public isEnabled = true;

  public hasStarted = false;

  private camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;

  private domElement: HTMLElement;

  private mouse = new THREE.Vector2();

  private target = new THREE.Vector3();

  private targetDistance = 1;

  private spherical = new THREE.Spherical();

  private lookDirection = new THREE.Vector3();

  private minZoom = 0.0001;

  private maxZoom = 10000;

  public minFov = 1.5;

  public maxFov = 110;

  private startMouse = new THREE.Vector2();

  private startSpherical = new THREE.Spherical();

  private upQuat = new THREE.Quaternion();

  private upQuatInverse = new THREE.Quaternion();

  public hspeed = 1;

  public vspeed = 1;

  public zoomStep = 0.9;

  private onMove: (() => void) | undefined;

  constructor(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    domElement: HTMLElement,
    onMove?: () => void
  ) {
    super();

    this.camera = camera;
    this.domElement = domElement;
    if (onMove) this.onMove = onMove;
    this.activate();
  }

  public activate = (): void => {
    this.domElement.addEventListener('pointermove', this.onPointerMove, { passive: false });
    this.domElement.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    this.domElement.addEventListener('pointerup', this.onPointerCancel, { passive: false });
    this.domElement.addEventListener('pointerleave', this.onPointerCancel, { passive: false });
    this.domElement.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.domElement.addEventListener('touchstart', this.onTouchStart, { passive: false });
    this.domElement.addEventListener('touchend', this.onTouchEnd, { passive: false });
    this.domElement.addEventListener('wheel', this.onMouseWheel, { passive: false });
  };

  public deactivate = (): void => {
    this.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.domElement.removeEventListener('pointerup', this.onPointerCancel);
    this.domElement.removeEventListener('pointerleave', this.onPointerCancel);
    this.domElement.removeEventListener('touchmove', this.onTouchMove);
    this.domElement.removeEventListener('touchstart', this.onTouchStart);
    this.domElement.removeEventListener('touchend', this.onTouchEnd);
    this.domElement.removeEventListener('wheel', this.onMouseWheel);
  };

  public dispose = (): void => this.deactivate();

  public lookAt = (target: THREE.Vector3) => {
    this.setTarget(target);

    this.hasStarted = false;
    this.camera.lookAt(target);
    this.camera.updateProjectionMatrix();
  };

  public getTarget = () => {
    return this.target;
  };

  public setTarget = (target: THREE.Vector3) => {
    this.target.copy(target);
    this.targetDistance = Math.max(1, this.camera.position.distanceTo(target));
  };

  public setUpQuat = (quat: THREE.Quaternion) => {
    this.upQuat.copy(quat);
    this.upQuatInverse.copy(quat).invert();
  };

  public calculateViewDirection = (worldDirection: THREE.Vector3) => {
    const { position } = this.camera;
    const lookAt = this.camera.position.clone();
    lookAt.addScaledVector(worldDirection, -this.targetDistance);
    return { position, lookAt };
  };

  private shouldReactOnEvent = (event: MouseEvent) => {
    return (
      event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
    );
  };

  private zoomOut = (zoomStep: number) => {
    const { camera, minZoom, maxZoom, maxFov } = this;
    if (camera instanceof THREE.PerspectiveCamera) {
      const newFov = camera.fov / zoomStep;
      if (newFov < maxFov) {
        camera.fov = newFov;
      }
    } else if (camera instanceof THREE.OrthographicCamera) {
      camera.zoom = Math.max(minZoom, Math.min(maxZoom, camera.zoom * zoomStep));
    }
    camera.updateProjectionMatrix();
    if (this.onMove) this.onMove();
  };

  private zoomIn = (zoomStep: number) => {
    const { camera, minZoom, maxZoom, minFov } = this;
    if (camera instanceof THREE.PerspectiveCamera) {
      const newFov = camera.fov * zoomStep;
      if (newFov > minFov) {
        camera.fov = newFov;
      }
    } else if (camera instanceof THREE.OrthographicCamera) {
      camera.zoom = Math.max(minZoom, Math.min(maxZoom, camera.zoom / zoomStep));
    }
    camera.updateProjectionMatrix();
    if (this.onMove) this.onMove();
  };

  private updateSphericalFromCamera = (): void => {
    this.camera.getWorldDirection(this.lookDirection);
    this.lookDirection.applyQuaternion(this.upQuatInverse);
    // Spherical_up is [0,1,0]
    // Transform world_up [0,0,1] to spherical_up by simple axes rotation (swap z, y)
    // const { x, y, z } = this.lookDirection;
    // this.spherical.setFromCartesianCoords(x, z, -y);

    const { x, y, z } = this.lookDirection;
    this.spherical.setFromCartesianCoords(x, y, z);
  };

  private updateMouse = (clientX: number, clientY: number): void => {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = (clientX - rect.left) / rect.width;
    this.mouse.y = (clientY - rect.top) / rect.height;
  };

  private start = (clientX: number, clientY: number): void => {
    this.updateMouse(clientX, clientY);
    this.updateSphericalFromCamera();

    this.startSpherical.copy(this.spherical);
    this.startMouse.copy(this.mouse);

    this.hasStarted = true;
  };

  private stop = (): void => {
    this.hasStarted = false;
  };

  private move = (clientX: number, clientY: number): void => {
    if (!this.hasStarted || !this.isEnabled) {
      return;
    }

    this.updateMouse(clientX, clientY);

    const fov = this.camera instanceof THREE.PerspectiveCamera ? this.camera.fov : 50;
    const aspect = this.camera instanceof THREE.PerspectiveCamera ? this.camera.aspect : 1;

    const newPhi =
      this.startSpherical.phi -
      (this.mouse.y - this.startMouse.y) * this.vspeed * ((fov * Math.PI) / 180);

    this.spherical.phi = clamp(newPhi, ZERO_LIMIT, Math.PI - ZERO_LIMIT);
    this.spherical.theta =
      this.startSpherical.theta +
      (this.mouse.x - this.startMouse.x) * this.hspeed * ((fov * aspect * Math.PI) / 180);

    this.target.setFromSphericalCoords(
      this.targetDistance,
      this.spherical.phi,
      this.spherical.theta
    );

    // transform back (y,z) from spherical up [0,1,0] to world up [0,0,1]
    // const { y, z } = this.target;
    // this.target.z = y;
    // this.target.y = -z;

    this.target.applyQuaternion(this.upQuat);

    this.target.add(this.camera.position);

    this.camera.lookAt(this.target);
    this.camera.updateProjectionMatrix();
    if (this.onMove) this.onMove();
  };

  private onMouseMove = (event: MouseEvent): void => {
    if (!this.hasStarted || !this.isEnabled) {
      return;
    }
    this.move(event.clientX, event.clientY);
  };

  private onMouseDown = (event: MouseEvent): void => {
    if (!this.isEnabled || !this.shouldReactOnEvent(event)) {
      return;
    }
    this.start(event.clientX, event.clientY);
  };

  private onMouseCancel = (): void => {
    if (!this.isEnabled) {
      return;
    }
    this.stop();
  };

  private onMouseWheel = (event: WheelEvent) => {
    if (!this.isEnabled) {
      return;
    }
    event.preventDefault();

    if (event.deltaY < 0) {
      this.zoomIn(this.zoomStep);
    } else if (event.deltaY > 0) {
      this.zoomOut(this.zoomStep);
    }
  };

  private onPointerMove = (event: PointerEvent): void => {
    switch (event.pointerType) {
      case 'mouse':
      case 'pen':
        this.onMouseMove(event);
        break;
      default:
    }
  };

  private onPointerDown = (event: PointerEvent): void => {
    switch (event.pointerType) {
      case 'mouse':
      case 'pen':
        this.onMouseDown(event);
        break;
      default:
    }
  };

  private onPointerCancel = (event: PointerEvent): void => {
    switch (event.pointerType) {
      case 'mouse':
      case 'pen':
        this.onMouseCancel();
        break;
      default:
    }
  };

  private onTouchMove = (event: TouchEvent): void => {
    if (!this.isEnabled) {
      return;
    }
    event.preventDefault();
    const touch = event.changedTouches[0];

    this.move(touch.clientX, touch.clientY);
  };

  private onTouchStart = (event: TouchEvent): void => {
    if (!this.isEnabled) {
      return;
    }
    event.preventDefault();
    const touch = event.changedTouches[0];

    this.start(touch.clientX, touch.clientY);
  };

  private onTouchEnd = (event: TouchEvent): void => {
    if (!this.isEnabled) {
      return;
    }
    event.preventDefault();

    this.stop();
  };
}
